'use client';

import { useEffect, useMemo, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';

const CropperCasted = Cropper as any; // eslint-disable-line @typescript-eslint/no-explicit-any

import { ToggleButtonGroup } from '../admin/ToggleButtonGroup';
import { FormDialog } from '../modal/FormDialog';
import type { PhotoUploadAspectChoice } from '../../hooks/photoUpload/types';

import styles from './photoUpload.module.scss';

export type ImageCropDialogProps = {
  isOpen: boolean;
  onClose: () => void;

  title?: string;
  imageSrc: string;

  aspect?: number;
  isBanner?: boolean;
  aspectChoices?: readonly PhotoUploadAspectChoice[];

  loading?: boolean;
  error?: string | null;

  showWatermarkToggle?: boolean;
  watermarkLabel?: string;
  watermarkEnabledDefault?: boolean;

  /** Show rotate ±90° buttons inside the dialog */
  showRotateControls?: boolean;

  onConfirm: (args: {
    croppedAreaPixels: Area;
    watermarkEnabled?: boolean;
    rotation?: number;
  }) => void;
};

export function ImageCropDialog({
  isOpen,
  onClose,
  title = 'Crop photo',
  imageSrc,
  aspect,
  isBanner,
  aspectChoices,
  loading = false,
  error = null,
  showWatermarkToggle = false,
  watermarkLabel = 'Add watermark',
  watermarkEnabledDefault = false,
  showRotateControls = false,
  onConfirm
}: ImageCropDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [watermarkEnabled, setWatermarkEnabled] = useState(
    watermarkEnabledDefault
  );
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [aspectChoiceIndex, setAspectChoiceIndex] = useState(0);
  const [naturalAspect, setNaturalAspect] = useState<number | null>(null);

  useEffect(() => {
    setImageLoadFailed(false);
    setNaturalAspect(null);
  }, [imageSrc]);

  const selectedAspectChoice = aspectChoices?.[aspectChoiceIndex];

  const displayError =
    error ??
    (imageLoadFailed
      ? "We couldn't read that image. Please try a different photo (JPG or PNG)."
      : null);

  const handleRotate = (deg: number) => {
    setRotation((r) => (r + deg + 360) % 360);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const resolvedAspect = useMemo(() => {
    if (selectedAspectChoice) {
      if (selectedAspectChoice.aspect !== 'original') {
        return selectedAspectChoice.aspect;
      }
      if (naturalAspect) return naturalAspect;
    }

    if (typeof aspect === 'number' && Number.isFinite(aspect) && aspect > 0) {
      return aspect;
    }

    // Reasonable defaults aligned with legacy behavior:
    // - banner: wide aspect
    // - normal: square-ish crop
    return isBanner ? 3 / 1 : 1;
  }, [aspect, isBanner, selectedAspectChoice, naturalAspect]);

  return (
    <FormDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      submitLabel={loading ? 'Uploading…' : 'Upload'}
      cancelLabel="Cancel"
      loading={loading}
      disableSubmit={!croppedAreaPixels || imageLoadFailed}
      onSubmit={(e) => {
        e.preventDefault();
        if (!croppedAreaPixels) return;
        onConfirm({
          croppedAreaPixels,
          watermarkEnabled: showWatermarkToggle ? watermarkEnabled : undefined,
          rotation: showRotateControls ? rotation : undefined
        });
      }}
    >
      <div className={styles.cropControls}>
        <img
          src={imageSrc}
          alt=""
          aria-hidden="true"
          style={{ display: 'none' }}
          onError={() => setImageLoadFailed(true)}
          onLoad={() => setImageLoadFailed(false)}
        />
        <div className={styles.cropContainer}>
          <CropperCasted
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={showRotateControls ? rotation : 0}
            aspect={resolvedAspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onMediaLoaded={(mediaSize: {
              naturalWidth: number;
              naturalHeight: number;
            }) => {
              if (mediaSize.naturalWidth > 0 && mediaSize.naturalHeight > 0) {
                setNaturalAspect(
                  mediaSize.naturalWidth / mediaSize.naturalHeight
                );
              }
            }}
            onCropComplete={(_croppedArea: Area, croppedPixels: Area) => {
              setCroppedAreaPixels(croppedPixels);
            }}
          />
        </div>

        {aspectChoices && aspectChoices.length > 1 ? (
          <div className={styles.sliderRow}>
            <span className={styles.sliderLabel}>Shape</span>
            <ToggleButtonGroup
              aria-label="Crop shape"
              options={aspectChoices.map((choice, index) => ({
                value: index,
                label: choice.label
              }))}
              value={aspectChoiceIndex}
              onChange={(index) => {
                setAspectChoiceIndex(index);
                setCrop({ x: 0, y: 0 });
                setZoom(1);
              }}
            />
          </div>
        ) : null}

        <div className={styles.sliderRow}>
          <label className={styles.sliderLabel} htmlFor="photo-upload-zoom">
            Zoom
          </label>
          <input
            id="photo-upload-zoom"
            className={styles.slider}
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </div>

        {showRotateControls && (
          <div className={styles.rotateRow}>
            <span className={styles.rotateLabel}>Rotate</span>
            <div className={styles.rotateBtns}>
              <button
                type="button"
                className={styles.rotateBtn}
                onClick={() => handleRotate(-90)}
                aria-label="Rotate 90° counter-clockwise"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ transform: 'scaleX(-1)' }}
                  aria-hidden="true"
                >
                  <path d="M21 2v6h-6" />
                  <path d="M21 13a9 9 0 1 1-3-7.7L21 8" />
                </svg>
                −90°
              </button>
              <button
                type="button"
                className={styles.rotateBtn}
                onClick={() => handleRotate(90)}
                aria-label="Rotate 90° clockwise"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 2v6h-6" />
                  <path d="M21 13a9 9 0 1 1-3-7.7L21 8" />
                </svg>
                +90°
              </button>
            </div>
          </div>
        )}

        {showWatermarkToggle ? (
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={watermarkEnabled}
              onChange={(e) => setWatermarkEnabled(e.target.checked)}
            />
            {watermarkLabel}
          </label>
        ) : null}

        {displayError ? (
          <div className={styles.cropError} role="alert">
            {displayError}
          </div>
        ) : null}
      </div>
    </FormDialog>
  );
}
